import type { BookingDetailsParticipant } from '../../domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { parseISO } from 'date-fns'
import { useIntl } from '@/intl'
import { formatDate } from '@/scopes/account/common/formatters'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { getParticipantAge } from '../../domain/participantAge'
import { useBookingDetailsStore } from '../../infrastructure/useBookingDetailsStore'
import { getParticipantsByStatus } from '../selectors'
import { formatParticipantFullName, getSupportedParticipantTitle } from '../utils/participantName'
import { BookingDetailsSection } from './BookingDetailsSection'
import { IconRow } from './IconRow'
import { ParticipantDetailsSheetPortal } from './ParticipantDetailsSheetPortal'
import { ParticipantEdit } from './ParticipantEdit/ParticipantEdit'
import styles from './ParticipantsSection.module.css'

export function ParticipantsSection() {
  const intl = useIntl()
  const locale = useConfiguration('locale')
  const { active: activeParticipants, cancelled: cancelledParticipants } = useBookingDetailsStore(getParticipantsByStatus)
  const hasCancelled = cancelledParticipants.length > 0
  const isSelfServiceEnabled = useBookingDetailsStore(state => state.isSelfServiceEnabled)
  const isGroupBooking = useBookingDetailsStore(state => state.isGroupBooking)
  const changeActiveParticipantId = useBookingDetailsStore(state => state.changeActiveParticipantId)
  const rawDepartureDate = useBookingDetailsStore(state => state.rawDepartureDate)

  function renderParticipant(participant: BookingDetailsParticipant) {
    const formattedBirthDate = formatDate(participant.birthDate, locale, 'long')
    const age = getParticipantAge(participant.birthDate, parseISO(rawDepartureDate))
    const ageLabel = age === null
      ? ''
      : age.unit === 'months'
        ? intl.formatMessage({ id: 'booking_details_participants_age_months' }, { count: age.count })
        : intl.formatMessage({ id: 'booking_details_participants_age_years' }, { count: age.count })
    const isMainBookerParticipantType = participant.type === 'MainBooker'
    const typeLabel = participant.type === 'Baby' || participant.type === 'Child'
      ? intl.formatMessage({ id: participant.type === 'Baby'
          ? 'booking_details_participant_type_baby'
          : 'booking_details_participant_type_child' })
      : null
    const titleKey = getSupportedParticipantTitle(participant.title)
    const buttonLabel = participant.isCancelled || !isSelfServiceEnabled
      ? intl.formatMessage({ id: 'booking_details_participants_view_button' })
      : intl.formatMessage({ id: 'booking_details_participants_view_and_edit_button' })

    return (
      <ListItem key={participant.id} variant="outlined" className={styles.participantItem}>
        <div className={styles.participantContent}>
          <div className={styles.participantInfo}>
            {isMainBookerParticipantType && !isGroupBooking && (
              <Tag variant="informative-tinted">
                {intl.formatMessage({ id: 'booking_details_participants_main_booker_tag' })}
              </Tag>
            )}
            {participant.isCancelled && (
              <Tag variant="danger-tinted">
                {intl.formatMessage({ id: 'common_cancelled' })}
              </Tag>
            )}
            <div>
              <Typography as="p" variant="body-m-bold-tight">
                {formatParticipantFullName(
                  titleKey ? intl.formatMessage({ id: titleKey }) : null,
                  participant.firstName,
                  participant.lastName,
                )}
              </Typography>
              {formattedBirthDate && (
                <Typography
                  className={styles.participantDetails}
                  as="p"
                  variant="body-s-regular-tight"
                >
                  {formattedBirthDate}
                  {ageLabel ? ` (${ageLabel})` : ''}
                  {typeLabel ? ` - ${typeLabel}` : ''}
                </Typography>
              )}
            </div>
          </div>
          <Button
            className={styles.participantButton}
            size="S"
            variant="secondary"
            onClick={() => changeActiveParticipantId(participant.id)}
          >
            {buttonLabel}
          </Button>
        </div>
      </ListItem>
    )
  }

  return (
    <BookingDetailsSection
      iconName="Family"
      title={intl.formatMessage({ id: 'common_participants' })}
      // TODO: When flight details are supported, use: "Zorg ervoor dat je gegevens precies overeenkomen met je paspoort, zo voorkom je vertraging bij het instappen."
      description={intl.formatMessage({ id: 'booking_details_participants_description' })}
    >
      {hasCancelled
        ? (
            <div className={styles.participantsGroups}>
              <div className={styles.participantsGroup}>
                <Typography as="p" variant="display-i-regular">
                  {intl.formatMessage({ id: 'booking_details_participants_active_section_title' })}
                </Typography>
                <div className={styles.participantsList}>
                  {activeParticipants.map(renderParticipant)}
                </div>
              </div>
              <div className={styles.participantsGroup}>
                <Typography as="p" variant="display-i-regular">
                  {intl.formatMessage({ id: 'booking_details_participants_cancelled_section_title' })}
                </Typography>
                <div className={styles.participantsList}>
                  {cancelledParticipants.map(renderParticipant)}
                </div>
              </div>
            </div>
          )
        : (
            <div className={styles.participantsList}>
              {activeParticipants.map(renderParticipant)}
            </div>
          )}
      <div className={styles.remark}>
        <IconRow
          glyph="InformationInCircle"
          description={intl.formatMessage({ id: 'booking_details_participants_age_description' })}
        />
      </div>
      <ParticipantDetailsSheetPortal />
      <ParticipantEdit />
    </BookingDetailsSection>
  )
}
